import { useLocation } from 'react-router-dom';
import LogRocket from 'logrocket';

import { toast } from '@utilities';

/** Query-string parameter used to scope a request (and the page) to a service model. */
export const SERVICE_NAME_QUERY_PARAM = 'serviceName';

export const EMPTY_SERVICE_NAME = '';

const parseServiceNameFromSearch = (search) => {
  // `URLSearchParams` is permissive and doesn't throw on well-formed router input;
  // the catch is a defensive boundary for non-spec runtimes (and to keep callers
  // free of error handling).
  try {
    return new URLSearchParams(search).get(SERVICE_NAME_QUERY_PARAM) || EMPTY_SERVICE_NAME;
  } catch {
    return EMPTY_SERVICE_NAME;
  }
};

/** Reads `?serviceName=<value>` from the current URL — empty string when absent or unreadable. */
export const readServiceNameFromUrl = () => parseServiceNameFromSearch(window.location.search);

// Returns a string primitive, so effect deps compare it by value — no
// memoization needed for stability.
export const useServiceNameFromUrl = () => {
  const { search } = useLocation();
  return parseServiceNameFromSearch(search);
};

/** True when `name` is non-empty and one of `allowedServices`. */
export const isValidServiceName = (name, allowedServices) =>
  Boolean(name) && allowedServices.includes(name);

/** Sets (or, when `name` is falsy, deletes) the `serviceName` param on `url`. Mutates and returns `url`. */
export const applyServiceNameToUrl = (url, name) => {
  if (name) {
    url.searchParams.set(SERVICE_NAME_QUERY_PARAM, name);
  } else {
    url.searchParams.delete(SERVICE_NAME_QUERY_PARAM);
  }
  return url;
};

/** Surfaces an invalid URL `?serviceName=<value>` to telemetry and the user. */
export const reportInvalidServiceName = ({ allowed, fallback, fromUrl }) => {
  LogRocket.track('ServiceSelector_Invalid_URL_ServiceName', {
    allowed: allowed.join(','),
    fallback,
    fromUrl,
  });
  toast.error(
    fallback
      ? `Service "${fromUrl}" isn't available to you — showing "${fallback}" instead.`
      : `Service "${fromUrl}" isn't available to you.`
  );
};
